import { useState, useMemo } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  FaPlus,
  FaSearch,
  FaReceipt,
  FaEdit,
  FaTrash,
  FaMoneyBillWave,
  FaCog,
  FaChevronLeft,
  FaChevronRight,
  FaEye
} from 'react-icons/fa';
import toast from 'react-hot-toast';
import GastoFormModal from '../components/egresos/GastoFormModal';
import CategoriasGastoModal from '../components/egresos/CategoriasGastoModal';
import ConfirmModal from '../components/shared/ConfirmModal';
import { GET_GASTOS, ELIMINAR_GASTO, MARCAR_GASTO_PAGADO, GET_RESUMEN_EGRESOS } from '../graphql/gastos';
import { GET_CATEGORIAS_GASTO, GET_PROVEEDORES } from '../graphql/proveedores';
import './InventarioPage.css';
import './Gastos.css';

/*
  Usage:
  <Route path="/gastos" element={<PrivateRoute><Gastos /></PrivateRoute>} />
*/

const formatCurrency = (value) =>
  new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0
  }).format(value || 0);

const formatFecha = (fecha) => {
  if (!fecha) return '—';
  return new Date(fecha).toLocaleDateString('es-CO', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

function getBaseUrl() {
  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4005/graphql';
  return apiUrl.replace(/\/graphql$/, '');
}

const LIMIT = 20;

const getPrimerDiaMes = () => {
  const hoy = new Date();
  return new Date(hoy.getFullYear(), hoy.getMonth(), 1).toISOString().slice(0, 10);
};

const getHoy = () => new Date().toISOString().slice(0, 10);

const Gastos = () => {
  const [busqueda, setBusqueda] = useState('');
  const [categoriaFiltro, setCategoriaFiltro] = useState('');
  const [proveedorFiltro, setProveedorFiltro] = useState('');
  const [pagadoFiltro, setPagadoFiltro] = useState('');
  const [fechaDesde, setFechaDesde] = useState(getPrimerDiaMes());
  const [fechaHasta, setFechaHasta] = useState(getHoy());
  const [offset, setOffset] = useState(0);
  const [mostrarGastoModal, setMostrarGastoModal] = useState(false);
  const [mostrarCategoriasModal, setMostrarCategoriasModal] = useState(false);
  const [gastoSeleccionado, setGastoSeleccionado] = useState(null);
  const [mostrarEliminarModal, setMostrarEliminarModal] = useState(false);
  const [mostrarPagarModal, setMostrarPagarModal] = useState(false);
  const [gastoAccion, setGastoAccion] = useState(null);
  const [vistaPrevia, setVistaPrevia] = useState(null);

  const pagadoVar = pagadoFiltro === 'si' ? true : pagadoFiltro === 'no' ? false : undefined;

  const { data, loading, refetch } = useQuery(GET_GASTOS, {
    variables: {
      busqueda: busqueda || null,
      categoria_gasto_id: categoriaFiltro ? parseInt(categoriaFiltro) : null,
      proveedor_id: proveedorFiltro ? parseInt(proveedorFiltro) : null,
      pagado: pagadoVar,
      fecha_desde: fechaDesde || null,
      fecha_hasta: fechaHasta || null,
      limit: LIMIT,
      offset
    },
    fetchPolicy: 'network-only'
  });

  const { data: dataResumen } = useQuery(GET_RESUMEN_EGRESOS, {
    variables: {
      fecha_desde: getPrimerDiaMes(),
      fecha_hasta: getHoy()
    },
    fetchPolicy: 'network-only'
  });

  const { data: dataCategorias } = useQuery(GET_CATEGORIAS_GASTO, {
    variables: { activa: true }
  });

  const { data: dataProveedores } = useQuery(GET_PROVEEDORES, {
    variables: { activo: true }
  });

  const [eliminarGasto] = useMutation(ELIMINAR_GASTO, {
    onCompleted: () => {
      toast.success('Gasto eliminado correctamente');
      refetch();
    },
    onError: (err) => toast.error(err.message)
  });

  const [marcarPagado] = useMutation(MARCAR_GASTO_PAGADO, {
    onCompleted: () => {
      toast.success('Gasto marcado como pagado');
      refetch();
    },
    onError: (err) => toast.error(err.message)
  });

  const gastos = data?.gastos?.gastos || [];
  const total = data?.gastos?.total || 0;
  const categorias = dataCategorias?.categoriasGasto || [];
  const proveedores = dataProveedores?.proveedores || [];
  const resumen = dataResumen?.resumenEgresos;

  const estadisticas = useMemo(() => {
    const totalMes = resumen?.total_gastos || 0;
    const pendientes = resumen?.gastos_pendientes_pago || 0;
    const totalGastosMes = gastos.reduce((acc, g) => acc + (g.total || 0), 0);
    const pagados = gastos.filter((g) => g.pagado).reduce((acc, g) => acc + (g.total || 0), 0);
    const promedio = gastos.length > 0 ? totalGastosMes / gastos.length : 0;
    return { totalMes, pendientes, pagados, promedio };
  }, [gastos, resumen]);

  const totalPaginas = Math.ceil(total / LIMIT);
  const paginaActual = Math.floor(offset / LIMIT) + 1;

  const handleNuevo = () => {
    setGastoSeleccionado(null);
    setMostrarGastoModal(true);
  };

  const handleEditar = (gasto) => {
    setGastoSeleccionado(gasto);
    setMostrarGastoModal(true);
  };

  const handleEliminar = (gasto) => {
    setGastoAccion(gasto);
    setMostrarEliminarModal(true);
  };

  const ejecutarEliminar = () => {
    if (!gastoAccion) return;
    eliminarGasto({ variables: { id: parseInt(gastoAccion.id) } });
    setMostrarEliminarModal(false);
    setGastoAccion(null);
  };

  const handleMarcarPagado = (gasto) => {
    setGastoAccion(gasto);
    setMostrarPagarModal(true);
  };

  const ejecutarMarcarPagado = () => {
    if (!gastoAccion) return;
    marcarPagado({
      variables: {
        id: parseInt(gastoAccion.id),
        fecha_pago: getHoy()
      }
    });
    setMostrarPagarModal(false);
    setGastoAccion(null);
  };

  const handleCerrarGastoModal = () => {
    setMostrarGastoModal(false);
    setGastoSeleccionado(null);
    refetch();
  };

  const handleCerrarCategoriasModal = () => {
    setMostrarCategoriasModal(false);
    refetch();
  };

  const handlePaginaAnterior = () => {
    if (offset >= LIMIT) setOffset(offset - LIMIT);
  };

  const handlePaginaSiguiente = () => {
    if (offset + LIMIT < total) setOffset(offset + LIMIT);
  };

  const handleFiltroChange = () => {
    setOffset(0);
  };

  return (
      <div className="inventario-page">
        {/* Header */}
        <div className="inventario-header">
          <div className="header-titulo">
            <FaReceipt className="header-icon" />
            <div>
              <h1>Gastos</h1>
              <p>Registro y seguimiento de gastos operativos</p>
            </div>
          </div>

          <div className="header-actions">
            <button className="btn-secondary" onClick={() => setMostrarCategoriasModal(true)}>
              <FaCog /> Categorias
            </button>
            <button className="btn-primary" onClick={handleNuevo}>
              <FaPlus /> Nuevo Gasto
            </button>
          </div>
        </div>

        {/* Estadisticas */}
        <div className="inventario-stats">
          <div className="stat-card">
            <div className="stat-value gasto-stat-currency">{formatCurrency(estadisticas.totalMes)}</div>
            <div className="stat-label">Total Mes</div>
          </div>
          <div className="stat-card stat-warning">
            <div className="stat-value gasto-stat-currency">{formatCurrency(estadisticas.pendientes)}</div>
            <div className="stat-label">Gastos Pendientes</div>
          </div>
          <div className="stat-card">
            <div className="stat-value gasto-stat-currency">{formatCurrency(estadisticas.pagados)}</div>
            <div className="stat-label">Gastos Pagados</div>
          </div>
          <div className="stat-card">
            <div className="stat-value gasto-stat-currency">{formatCurrency(estadisticas.promedio)}</div>
            <div className="stat-label">Promedio por Gasto</div>
          </div>
        </div>

        {/* Filtros */}
        <div className="inventario-filtros">
          <div className="filtro-busqueda">
            <FaSearch />
            <input
              type="text"
              placeholder="Buscar por descripcion o codigo..."
              value={busqueda}
              onChange={(e) => {
                setBusqueda(e.target.value);
                handleFiltroChange();
              }}
            />
          </div>

          <select
            className="filtro-select"
            value={categoriaFiltro}
            onChange={(e) => {
              setCategoriaFiltro(e.target.value);
              handleFiltroChange();
            }}
          >
            <option value="">Todas las categorias</option>
            {categorias.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.nombre}
              </option>
            ))}
          </select>

          <select
            className="filtro-select"
            value={proveedorFiltro}
            onChange={(e) => {
              setProveedorFiltro(e.target.value);
              handleFiltroChange();
            }}
          >
            <option value="">Todos los proveedores</option>
            {proveedores.map((prov) => (
              <option key={prov.id} value={prov.id}>
                {prov.nombre}
              </option>
            ))}
          </select>

          <select
            className="filtro-select filtro-select-sm"
            value={pagadoFiltro}
            onChange={(e) => {
              setPagadoFiltro(e.target.value);
              handleFiltroChange();
            }}
          >
            <option value="">Todos</option>
            <option value="si">Pagados</option>
            <option value="no">Pendientes</option>
          </select>

          <div className="gasto-filtro-fechas">
            <label className="gasto-fecha-label">Desde</label>
            <input
              type="date"
              className="filtro-fecha"
              value={fechaDesde}
              onChange={(e) => {
                setFechaDesde(e.target.value);
                handleFiltroChange();
              }}
            />
          </div>

          <div className="gasto-filtro-fechas">
            <label className="gasto-fecha-label">Hasta</label>
            <input
              type="date"
              className="filtro-fecha"
              value={fechaHasta}
              onChange={(e) => {
                setFechaHasta(e.target.value);
                handleFiltroChange();
              }}
            />
          </div>
        </div>

        {/* Tabla */}
        <div className="inventario-tabla-container">
          {loading ? (
            <div className="loading-state">Cargando gastos...</div>
          ) : gastos.length === 0 ? (
            <div className="empty-state">
              <FaReceipt />
              <p>No hay gastos para mostrar</p>
              <button className="btn-primary" onClick={handleNuevo}>
                <FaPlus /> Registrar primer gasto
              </button>
            </div>
          ) : (
            <table className="inventario-tabla">
              <thead>
                <tr>
                  <th>Codigo</th>
                  <th>Fecha</th>
                  <th>Descripcion</th>
                  <th>Categoria</th>
                  <th>Proveedor</th>
                  <th>Total</th>
                  <th>Pagado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {gastos.map((gasto) => (
                  <tr key={gasto.id}>
                    <td>
                      <span className="codigo">{gasto.codigo}</span>
                    </td>
                    <td>{formatFecha(gasto.fecha)}</td>
                    <td>
                      <div className="item-nombre">
                        <strong>{gasto.descripcion}</strong>
                        {gasto.numero_factura_proveedor && (
                          <small>Fact. {gasto.numero_factura_proveedor}</small>
                        )}
                      </div>
                    </td>
                    <td>
                      {gasto.categoria_gasto ? (
                        <span className="gasto-categoria-badge">
                          <span
                            className="categoria-dot"
                            style={{ backgroundColor: gasto.categoria_gasto.color || '#6b7280' }}
                          />
                          {gasto.categoria_gasto.nombre}
                        </span>
                      ) : (
                        <span className="sin-categoria">Sin categoria</span>
                      )}
                    </td>
                    <td>
                      {gasto.proveedor ? (
                        <span>{gasto.proveedor.nombre}</span>
                      ) : (
                        <span className="sin-categoria">—</span>
                      )}
                    </td>
                    <td>
                      <strong>{formatCurrency(gasto.total)}</strong>
                    </td>
                    <td>
                      {gasto.pagado ? (
                        <span className="estado-badge estado-activo">Si</span>
                      ) : (
                        <button
                          className="btn-accion btn-pagar"
                          onClick={() => handleMarcarPagado(gasto)}
                          title="Marcar como pagado"
                        >
                          <FaMoneyBillWave />
                        </button>
                      )}
                    </td>
                    <td>
                      <div className="acciones">
                        {gasto.comprobante_url && (
                          <button
                            className="btn-accion btn-ver-adjunto"
                            onClick={() => setVistaPrevia(gasto.comprobante_url)}
                            title="Ver archivo adjunto"
                          >
                            <FaEye />
                          </button>
                        )}
                        <button
                          className="btn-accion btn-editar"
                          onClick={() => handleEditar(gasto)}
                          title="Editar gasto"
                        >
                          <FaEdit />
                        </button>
                        <button
                          className="btn-accion btn-eliminar"
                          onClick={() => handleEliminar(gasto)}
                          title="Eliminar gasto"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Paginacion */}
        {total > LIMIT && (
          <div className="gasto-paginacion">
            <span className="paginacion-info">
              Mostrando {offset + 1}–{Math.min(offset + LIMIT, total)} de {total} gastos
            </span>
            <div className="paginacion-controles">
              <button
                className="btn-paginacion"
                onClick={handlePaginaAnterior}
                disabled={offset === 0}
              >
                <FaChevronLeft />
              </button>
              <span className="paginacion-pagina">
                {paginaActual} / {totalPaginas}
              </span>
              <button
                className="btn-paginacion"
                onClick={handlePaginaSiguiente}
                disabled={offset + LIMIT >= total}
              >
                <FaChevronRight />
              </button>
            </div>
          </div>
        )}

        {/* Modales */}
        {mostrarGastoModal && (
          <GastoFormModal
            isOpen={mostrarGastoModal}
            onClose={handleCerrarGastoModal}
            gasto={gastoSeleccionado}
          />
        )}

        {mostrarCategoriasModal && (
          <CategoriasGastoModal
            isOpen={mostrarCategoriasModal}
            onClose={handleCerrarCategoriasModal}
          />
        )}

        <ConfirmModal
          isOpen={mostrarEliminarModal}
          onClose={() => { setMostrarEliminarModal(false); setGastoAccion(null); }}
          onConfirm={ejecutarEliminar}
          title="Eliminar Gasto"
          message={`¿Deseas eliminar el gasto "${gastoAccion?.descripcion || gastoAccion?.codigo}"? Esta accion no se puede deshacer.`}
          variant="danger"
          confirmText="Eliminar"
        />

        <ConfirmModal
          isOpen={mostrarPagarModal}
          onClose={() => { setMostrarPagarModal(false); setGastoAccion(null); }}
          onConfirm={ejecutarMarcarPagado}
          title="Marcar como Pagado"
          message={`¿Marcar el gasto ${gastoAccion?.codigo} (${formatCurrency(gastoAccion?.total)}) como pagado?`}
          variant="success"
          confirmText="Marcar Pagado"
        />

        {/* Visor de comprobante */}
        {vistaPrevia && (() => {
          const baseUrl = getBaseUrl();
          const fullUrl = vistaPrevia.startsWith('http') ? vistaPrevia : `${baseUrl}${vistaPrevia}`;
          const esPdf = vistaPrevia.toLowerCase().endsWith('.pdf');
          return (
            <div className="comprobante-lightbox" onClick={() => setVistaPrevia(null)}>
              <div className="comprobante-lightbox__content" onClick={(e) => e.stopPropagation()}>
                <button className="comprobante-lightbox__close" onClick={() => setVistaPrevia(null)}>✕</button>
                {esPdf ? (
                  <iframe src={fullUrl} className="comprobante-lightbox__iframe" title="Comprobante PDF" />
                ) : (
                  <img src={fullUrl} alt="Comprobante" className="comprobante-lightbox__img" />
                )}
              </div>
            </div>
          );
        })()}
      </div>
  );
};

export default Gastos;
